import { useCallback, useEffect, useState } from 'react';
import type { IEvent } from 'fabric/fabric-impl';
import { useActiveCanvas, useIsPodProductCode } from '../../../hooks';
import { isPhotoTextZone } from '../../../utils';
import { isFloatingToolMenuInteraction } from '../../floating-tool-menu/floating-tool-menu-host';

/**
 * Custom hook used in toolbar component to handle logic to show and hide buttons.
 * This hook checks the project type code and the active canvas object to determine if the buttons should be displayed
 * @returns showToolbar: true if the toolbar buttons should be displayed
 * @returns selectedZoneHasContent: true if the selected PhotoTextZone already has content
 */
export const useShowToolbar = () => {
  const canvas = useActiveCanvas();
  const isPhotoCard = useIsPodProductCode();
  const [showToolbar, setShowToolbar] = useState(false);
  const [selectedZoneHasContent, setSelectedZoneHasContent] = useState(false);

  const handleSelection = useCallback((options: IEvent<Event>) => {
    const selectedObject = options.selected?.[0];
    if (selectedObject && isPhotoTextZone(selectedObject)) {
      setShowToolbar(true);
      setSelectedZoneHasContent(!!selectedObject.data?.hasContent);
      return;
    }
    setShowToolbar(false);
    setSelectedZoneHasContent(false);
  }, []);

  const handleClearSelection = useCallback((options?: IEvent<Event>) => {
    const target = options?.e?.target;
    if (isFloatingToolMenuInteraction(target instanceof Element ? target : null)) {
      return;
    }

    setShowToolbar(false);
    setSelectedZoneHasContent(false);
  }, []);

  useEffect(() => {
    if (isPhotoCard) {
      return;
    }
    canvas?.current?.on('selection:created', handleSelection);
    canvas?.current?.on('selection:updated', handleSelection);
    canvas?.current?.on('selection:cleared', handleClearSelection);
    return () => {
      canvas?.current?.off('selection:created', handleSelection);
      canvas?.current?.off('selection:updated', handleSelection);
      canvas?.current?.off('selection:cleared', handleClearSelection);
    };
  }, [canvas, handleClearSelection, handleSelection, isPhotoCard]);

  return {
    showToolbar: isPhotoCard ? true : showToolbar,
    selectedZoneHasContent,
  };
};
